import React, { useCallback, useContext, useEffect, useState } from "react";

import { Button, CircularProgress, Grid, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { PlanType } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/admin/v1/subscriptions_pb";
import {
  ComponentPrice,
  PriceTier,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/components_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { ChargifyState } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import { SubscriptionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { PermissionsContext } from "../../../../context/permissions-context";
import { toUUID } from "../../../../helpers/s12-helpers";
import { PricePointChanges } from "./../../../../constants/chargify";
import CurrencyFormatter from "./../../../shared/currency-formatter";
import ChangeComponentPricePointConfirm from "./change-component-price-point-confirmation";
import ChangeComponentPricePointToEnterprise from "./change-component-price-point-to-enterprise";
import ComponentPricePicker from "./component-price-picker";
import SubscriptionAction from "./subscription-action";
import { getSubscriptionComponentBySubId } from "api/endpoints/components";
import { getOrgContractList } from "api/endpoints/contracts";
import { callOptionsHeaders } from "api/helpers";
import { componentTypeDisplayMapping } from "constants/component";
import { currencyMapping } from "constants/index";
import { ContractsListContext } from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import { getComponentPriceLabel } from "helpers/get-component-price-label";
import { getSubscriptionCurrency } from "helpers/subscriptions";
import {
  ChangeComponentPriceWrapperProps,
  UrlParamTypes,
} from "types/org-display";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40rem",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
};

const checkPricePointChange = (
  currPrice: ComponentPrice | undefined,
  newPrice: ComponentPrice | undefined,
) => {
  if (!currPrice || !newPrice) {
    return;
  }
  if (
    currPrice?.priceTier === PriceTier.PREMIUM &&
    newPrice?.priceTier === PriceTier.ENTERPRISE
  ) {
    return PricePointChanges.PREMIUM_TO_ENTERPRISE;
  }
};

const ChangeComponentPricePoint = ({
  subscription,
  onPlanTypeUpdate,
  componentType,
}: ChangeComponentPriceWrapperProps) => {
  const classes = mainStyles();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showProcessingPage, setShowProcessingPage] = useState(false);
  const [seatQuantity, setSeatQuantity] = useState<number>(0);
  const [newPlanType, setNewPlanType] = useState<PlanType>(
    PlanType.UNSPECIFIED,
  );
  const { hasPermissions, permissions } = useContext(PermissionsContext);
  const canAccess =
    !permissions.isPermissionsLoading &&
    hasPermissions([
      ActionType.UPDATE_SUBSCRIPTION_COMPONENT_PRICE,
      ActionType.CREATE_ENTERPRISE_METADATA,
    ]);
  const { setContractsList } = useContext(ContractsListContext);

  const [newPrice, setNewPrice] = useState<ComponentPrice | undefined>();
  const [currPrice, setCurrPrice] = useState<ComponentPrice | undefined>();
  const [componentId, setComponentId] = useState<number>(0);
  const subId = subscription?.id;
  const chargifyId = subscription?.chargify?.id || "";
  const chargifySite = subscription?.chargify?.site || "";
  const currency = getSubscriptionCurrency(subscription);
  const currentCurrency = currency
    ? currencyMapping[currency as keyof typeof currencyMapping]
    : undefined;
  const { showError } = useContext(SnackbarContext);
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);

  const isFreeOrg =
    subscription &&
    (subscription.type === SubscriptionType.FREE ||
      subscription.type === SubscriptionType.FREE_ORG);
  const subActive = subscription && subscription.active === true;
  const chargifyStateUnknownOrCanceled =
    subscription &&
    subscription.chargify &&
    [ChargifyState.CANCELED, ChargifyState.UNSPECIFIED].includes(
      subscription.chargify.state,
    );
  const isActivePaid =
    subscription &&
    subscription.chargify &&
    [ChargifyState.ACTIVE, ChargifyState.PAST_DUE].includes(
      subscription.chargify.state,
    );
  const changeFromPremiumToEnterprise =
    checkPricePointChange(currPrice, newPrice) ===
    PricePointChanges.PREMIUM_TO_ENTERPRISE;

  const getCurrentSubscriptionComponent = useCallback(() => {
    setIsLoading(true);
    setIsSubscriptionActionLoading(true);
    getSubscriptionComponentBySubId(
      {
        subId: toUUID(subId),
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((res) => {
        for (const c of res?.subscriptionComponents) {
          if (c.componentType == componentType) {
            setCurrPrice(c.componentPrice[0]);
            setSeatQuantity(c.quantity);
            setComponentId(c.id);
          }
        }
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
        setIsSubscriptionActionLoading(false);
      });
  }, [regionCode, roleId, subId, showError, componentType]);

  useEffect(() => {
    if (!isFreeOrg) {
      getCurrentSubscriptionComponent();
    }
  }, [getCurrentSubscriptionComponent, isFreeOrg]);

  const handleModalClose = (event: object, reason: string) => {
    // disable click outside of modal while component change is progressing.
    if (isProcessing && event && reason === "backdropClick") {
      const warning =
        "Please do not close while processing, if any issue or error, please contact #business-systems team slack channel";
      alert(warning);
      return false;
    } else {
      setShowModal(false);
      setShowProcessingPage(false);
    }
  };

  const priceChangeHandler = (newComponentPrice: ComponentPrice) => {
    setNewPrice(newComponentPrice);
    if (newComponentPrice.priceTier === PriceTier.ENTERPRISE) {
      setNewPlanType(PlanType.ENTERPRISE);
    }
  };

  const currentSeatPriceMessage = () => {
    if (currPrice === undefined) {
      return null;
    }

    return (
      <Grid
        className={clsx(classes.spaceTopSmall)}
        data-testid="change-component-price-point-current-price"
      >
        {`Current price is ${getComponentPriceLabel(currPrice, "seat")}`}
      </Grid>
    );
  };

  const renderComponentPricePicker = () => {
    const pricePicker = currentSeatPriceMessage();

    return (
      <div style={{ fontSize: "14px" }}>
        {pricePicker}
        <ComponentPricePicker
          subscription={subscription}
          componentId={componentId}
          onComponentPriceChange={priceChangeHandler}
          currentPricePoint={currPrice}
        />
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={() => setShowProcessingPage(true)}
          data-testid="change-component-price-confirm-btn"
          disabled={!newPrice || currPrice?.id === newPrice?.id}
        >
          Update Component Price
        </Button>
      </div>
    );
  };

  const componentChangeCompleted = () => {
    setCurrPrice(newPrice);
    setIsProcessing(false);
    onPlanTypeUpdate(newPlanType);
    handleModalClose({}, "normal");

    if (subActive && isActivePaid && !isFreeOrg) {
      getOrgContractList(
        { orgId: roleId },
        callOptionsHeaders(regionCode, roleId),
      )
        .then((res) => {
          setContractsList(res);
        })
        .catch(showError);
    }
  };

  const onStartProcess = () => {
    setIsProcessing(true);
  };

  if (
    !subscription ||
    isFreeOrg ||
    !subActive ||
    chargifyStateUnknownOrCanceled
  ) {
    return null;
  }

  const renderUpdatePricePointForm = () => {
    if (!showProcessingPage) {
      return renderComponentPricePicker();
    } else {
      if (!currPrice || !newPrice) {
        return null;
      } else if (changeFromPremiumToEnterprise) {
        return (
          <ChangeComponentPricePointToEnterprise
            chargifyId={chargifyId}
            chargifySite={chargifySite}
            subscription={subscription}
            onStartProcess={onStartProcess}
            onComplete={componentChangeCompleted}
            currPrice={currPrice}
            newPrice={newPrice}
            currency={currentCurrency || 0}
            quantity={seatQuantity}
            isFreeOrg={isFreeOrg}
            subActive={subActive}
          />
        );
      } else {
        return (
          <ChangeComponentPricePointConfirm
            currPrice={currPrice}
            newPrice={newPrice}
            subId={subscription.id}
            chargifySite={chargifySite}
            onCancel={handleModalClose}
            onComplete={componentChangeCompleted}
          />
        );
      }
    }
  };
  const componentTypeId = componentTypeDisplayMapping[componentType]
    .replace(/\s+/g, "-")
    .toLowerCase();
  return (
    <SubscriptionAction
      title={componentTypeDisplayMapping[componentType] + " Price"}
      middleElem={
        <>
          <Typography
            sx={classes.spaceTop}
            data-testid={`change-${componentTypeId}-seat-price`}
          >
            {currPrice ? (
              <CurrencyFormatter
                priceInCents={currPrice?.price || 0}
                currency={currentCurrency || 0}
              />
            ) : (
              <>N/A</>
            )}
          </Typography>
        </>
      }
    >
      {isLoading ? (
        <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
          <CircularProgress
            color="primary"
            variant="indeterminate"
            thickness={4}
            size={30}
            data-testid="loading-icon"
          />
        </Grid>
      ) : (
        <>
          <Button
            sx={{ mt: 1, mr: 2 }}
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => setShowModal(true)}
            data-testid={`change-${componentTypeId}-component-price-btn`}
            disabled={
              !canAccess || seatQuantity == 0 || isSubscriptionActionLoading
            }
          >
            Change {componentTypeDisplayMapping[componentType]} Price
          </Button>

          {showModal && (
            <Modal
              open={showModal}
              onClose={handleModalClose}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
              data-testid="change-component-price-modal"
            >
              <Box sx={style}>{renderUpdatePricePointForm()}</Box>
            </Modal>
          )}
        </>
      )}
    </SubscriptionAction>
  );
};

export default ChangeComponentPricePoint;
